import { type AnyAction } from '@reduxjs/toolkit'

export interface NotificationInAppAction {
  label: string
  path: string
}

export interface NotificationExternalAction {
  label: string
  href: string
}

export interface NotificationDispatchAction {
  label: string
  dispatch: AnyAction
}

export type NotificationAction = NotificationInAppAction | NotificationExternalAction

export const isExternalAction = (
  action: NotificationAction,
): action is NotificationExternalAction => 'href' in action && action.href !== ''

export const isDispatchAction = (
  action: NotificationAction | NotificationDispatchAction,
): action is NotificationDispatchAction => 'dispatch' in action

export interface NotificationExtra extends Record<string, unknown> {
  delay: number
  action: NotificationAction | NotificationDispatchAction
  actions: NotificationAction[]
}

export interface Notification<Type = string, Extra = Partial<NotificationExtra>> {
  id: string
  message: string
  readonly type: Type
  extra?: Extra
}

export type SuccessNotification<Extra = Partial<NotificationExtra>> = Notification<'SUCCESS', Extra>

export type ErrorNotification<Extra = Partial<NotificationExtra>> = Notification<'ERROR', Extra>

export type ProgressNotification<Extra = Partial<NotificationExtra>> = Notification<
  'PROGRESS',
  Extra
>

// Carries no message: the alert renders its own copy from i18n. Behaviour stays
// out of the store so the queue remains serialisable — see decisions.md D-7.
export type FeedbackNotification<Extra = Partial<NotificationExtra>> = Notification<
  'FEEDBACK',
  Extra
>

export type AnyNotification =
  | SuccessNotification
  | ErrorNotification
  | ProgressNotification
  | FeedbackNotification

export type NotificationType = AnyNotification['type']

export interface NotificationsSlice {
  notifications: Notification[]
}
